/**
 * Design System Tokens
 *
 * Single source of truth for all design decisions:
 * spacing, typography, border radii, shadows, z-indices, and animation durations.
 *
 * These complement the colour palette (palette.ts) and theme system (useTheme.ts).
 */

// ---------------------------------------------------------------------------
// Spacing
// Use multiples of 4 for a consistent rhythm.
// ---------------------------------------------------------------------------
export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  9: 36,
  10: 40,
  12: 48,
  14: 56,
  16: 64,
  20: 80,
  24: 96,
} as const;

export type SpacingKey = keyof typeof spacing;

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------
export const fontSize = {
  '2xs': 10,
  xs: 12,
  sm: 13,
  base: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 28,
  '4xl': 32,
  '5xl': 36,
} as const;

export const fontWeight = {
  light: '300' as const,
  regular: '400' as const,
  medium: '500' as const,
  semibold: '600' as const,
  bold: '700' as const,
  extrabold: '800' as const,
} as const;

export const lineHeight = {
  tight: 1.2,
  snug: 1.35,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
} as const;

export const letterSpacing = {
  tight: -0.5,
  normal: 0,
  wide: 0.5,
  wider: 1,
} as const;

// ---------------------------------------------------------------------------
// Border Radii
// ---------------------------------------------------------------------------
export const radii = {
  none: 0,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  '3xl': 24,
  full: 9999,
} as const;

export type RadiusKey = keyof typeof radii;

// ---------------------------------------------------------------------------
// Shadows (React Native compatible)
// ---------------------------------------------------------------------------
export const shadows = {
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  xs: {
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sm: {
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  md: {
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  lg: {
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
  },
  xl: {
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 20,
    elevation: 10,
  },
} as const;

// ---------------------------------------------------------------------------
// Z-Index
// ---------------------------------------------------------------------------
export const zIndex = {
  base: 0,
  raised: 10,
  dropdown: 100,
  sticky: 200,
  overlay: 300,
  modal: 400,
  toast: 500,
  tooltip: 600,
} as const;

// ---------------------------------------------------------------------------
// Animation Durations (ms)
// ---------------------------------------------------------------------------
export const duration = {
  instant: 0,
  fast: 150,
  normal: 250,
  slow: 400,
  slower: 600,
} as const;

// ---------------------------------------------------------------------------
// Component Size Presets
// Used for consistent interactive element sizing.
// ---------------------------------------------------------------------------
export const componentSize = {
  button: {
    sm: { height: 36, paddingHorizontal: spacing[3], fontSize: fontSize.base },
    md: { height: 44, paddingHorizontal: spacing[5], fontSize: fontSize.md },
    lg: { height: 52, paddingHorizontal: spacing[6], fontSize: fontSize.md },
  },
  input: {
    sm: { height: 36, paddingHorizontal: spacing[3] },
    md: { height: 44, paddingHorizontal: spacing[4] },
    lg: { height: 52, paddingHorizontal: spacing[4] },
  },
  icon: {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
    xl: 40,
  },
  colorSwatch: {
    sm: 32,
    md: 40,
    lg: 56,
    xl: 80,
  },
} as const;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export const layout = {
  containerMaxWidth: 1200,
  contentMaxWidth: 800,
  sidebarWidth: 240,
  bottomNavHeight: 80,
  headerHeight: 60,
  cardPadding: spacing[4],
  screenPadding: {
    mobile: spacing[5],
    desktop: spacing[6],
  },
} as const;
